import React, { useState, useEffect, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import MapView from "./components/MapView";
import Controls from "./components/Controls";
import Home from "./components/Home";

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  overflow: hidden;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
  color: #000;
  font-family: "Inter", sans-serif;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid rgb(183 183 183 / 30%);
  border-radius: 50%;
  border-top-color: black;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 20px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  margin: 10px 0 0 0;
  font-size: 1rem;
  opacity: 0.8;
  text-align: center;
`;

export default function App() {
  const navigate = useNavigate();

  const [basemap, setBasemap] = useState("maptiler-custom"); // Default to hiking-optimized map (use opentopomap for dev)
  const [geojsonData, setGeojsonData] = useState([]);
  const [showMarkers, setShowMarkers] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [hoverLocation, setHoverLocation] = useState(null);
  const [regionStats, setRegionStats] = useState({});
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDemo, setIsDemo] = useState(false);

  const checkAuthentication = () => {
    setLoading(true);
    fetch("/auth/me")
      .then((res) => {
        if (!res.ok) {
          throw new Error("Not authenticated");
        }
        return res.json();
      })
      .then((data) => {
        setUser(data);
        setLoading(false);
        fetchRegions();
      })
      .catch(() => {
        setUser(null);
        setLoading(false);
        navigate("/login");
      });
  };

  useEffect(() => {
    checkAuthentication();
  }, []);

  const fetchRegions = async () => {
    try {
      const res = await fetch("/api/regions");
      const data = await res.json();
      setRegions(data);
    } catch (err) {
      console.error("Failed to fetch regions:", err);
    }
  };

  // Memoize the selected region metadata to prevent unnecessary re-renders
  const selectedRegionMetaData = useMemo(() => {
    if (!selectedRegionId || !regions.length) return null;
    return regions.find((region) => region.id === selectedRegionId);
  }, [selectedRegionId, regions]);

  useEffect(() => {
    if (!selectedRegionId) return;

    setGeojsonData([]);

    async function loadData() {
      try {
        const res = await fetch(
          `/api/region/${selectedRegionId}?isDemo=${isDemo}`
        );
        const { processedFeatures, stats } = await res.json();

        setGeojsonData(processedFeatures);
        setRegionStats(stats);
      } catch (err) {
        console.error("Failed to fetch region data:", err);
      }
    }
    loadData();
  }, [selectedRegionId, isDemo, regions]);

  if (loading) {
    return (
      <>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading your mountain adventures...</LoadingText>
        </LoadingContainer>
      </>
    );
  }

  if (selectedRegionId && geojsonData.length === 0) {
    return (
      <>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>
            Traveling to {selectedRegionMetaData?.name},{" "}
            {selectedRegionMetaData?.location}...
          </LoadingText>
        </LoadingContainer>
      </>
    );
  }

  return (
    <>
      <Container>
        {regions && !selectedRegionId ? (
          <Home
            regions={regions}
            setSelectedRegionId={setSelectedRegionId}
            fetchRegions={fetchRegions}
            setIsDemo={setIsDemo}
          />
        ) : (
          <>
            <MapView
              basemap={basemap}
              geojsonData={geojsonData}
              showMarkers={showMarkers}
              hoverLocation={hoverLocation}
              selectedRegionId={selectedRegionId}
              visualizationSettings={selectedRegionMetaData?.visualization}
            />
            <Controls
              basemap={basemap}
              setBasemap={setBasemap}
              showMarkers={showMarkers}
              setShowMarkers={setShowMarkers}
              regionMetaData={selectedRegionMetaData}
              regionStats={regionStats}
              setSelectedRegionId={setSelectedRegionId}
              onHoverLocation={setHoverLocation}
              isAdmin={user.isAdmin}
            />
          </>
        )}
      </Container>
    </>
  );
}
